// Aurora Innovation, Inc. Proprietary and Confidential. Copyright 2026.
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { formHelperTextClasses } from '@mui/material/FormHelperText';
import { formLabelClasses } from '@mui/material/FormLabel';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { selectClasses } from '@mui/material/Select';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { typographyClasses } from '@mui/material/Typography';
import { rem } from '../../tokens/conversions';
import { borderRadius, spacing } from '../../tokens/spacing';
import { textFieldColors } from './textFieldColors';
export var TextField = function(mode) {
    var c = textFieldColors(mode);
    var _obj, _obj1, _obj2, _obj3, _obj4, _obj5, _obj6, _obj7, _obj8, _obj9, _obj10, _obj11, _obj12, _obj13;
    var overrides = {
        MuiFormControl: {
            styleOverrides: {
                root: (_obj = {}, // Reserves space for the floating label inside FormControl's padding area.
                // Uses paddingTop (not marginTop) to avoid conflicting with MUI Stack's
                // margin-based spacing selectors. Value = label lineHeight + 4px gap (Figma).
                _define_property(_obj, "&:has(.".concat(inputLabelClasses.shrink, ")"), {
                    paddingTop: rem(22)
                }), _define_property(_obj, "&:has(.".concat(inputLabelClasses.sizeSmall, ".").concat(inputLabelClasses.shrink, ")"), {
                    paddingTop: rem(18)
                }), _obj)
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&&': {
                        borderRadius: borderRadius.tiny,
                        color: c.default.text
                    }
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: (_obj1 = {
                    color: c.default.labelText
                }, _define_property(_obj1, "&.".concat(formLabelClasses.error), {
                    color: c.error.labelText
                }), _define_property(_obj1, "&.".concat(formLabelClasses.disabled), {
                    color: c.disabled.labelText
                }), _obj1),
                asterisk: _define_property({
                    color: 'inherit'
                }, "&.".concat(formLabelClasses.error), {
                    color: 'inherit'
                })
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                outlined: (_obj2 = {
                    color: c.default.labelText,
                    fontSize: rem(14),
                    fontWeight: '400',
                    lineHeight: rem(18)
                }, _define_property(_obj2, "&.".concat(inputLabelClasses.shrink), {
                    transform: 'none'
                }), _define_property(_obj2, "&.".concat(inputLabelClasses.sizeSmall), _define_property({
                    fontSize: rem(12),
                    lineHeight: rem(16)
                }, "&.".concat(inputLabelClasses.shrink), {
                    transform: 'none'
                })), _obj2)
            },
            defaultProps: {
                shrink: true
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: (_obj4 = {
                    margin: 0
                }, _define_property(_obj4, "& .".concat(typographyClasses.root), {
                    fontSize: rem(14),
                    fontWeight: '400',
                    lineHeight: rem(18),
                    color: c.default.labelText
                }), _define_property(_obj4, "& .".concat(svgIconClasses.root), {
                    fontSize: rem(20),
                    color: c.default.labelText
                }), _define_property(_obj4, "&.".concat(inputAdornmentClasses.sizeSmall), (_obj3 = {}, _define_property(_obj3, "& .".concat(svgIconClasses.root), {
                    fontSize: rem(12)
                }), _define_property(_obj3, "& .".concat(typographyClasses.root), {
                    fontSize: rem(12),
                    fontWeight: '400',
                    lineHeight: rem(16)
                }), _obj3)), _obj4)
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                contained: {
                    color: c.default.hintText,
                    marginLeft: 0,
                    marginTop: rem(4),
                    fontSize: rem(14),
                    fontWeight: '400',
                    lineHeight: rem(18)
                },
                root: (_obj5 = {}, _define_property(_obj5, "&.".concat(formHelperTextClasses.disabled), {
                    color: c.disabled.hintText
                }), _define_property(_obj5, "&.".concat(formHelperTextClasses.error), {
                    color: c.error.hintText
                }), _define_property(_obj5, "&.".concat(formHelperTextClasses.sizeSmall), {
                    fontSize: rem(12),
                    lineHeight: rem(16),
                    marginTop: rem(2)
                }), _obj5)
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: (_obj11 = {
                    backgroundColor: c.default.background,
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(spacing[2]),
                    padding: rem(spacing[2]),
                    height: rem(36)
                }, _define_property(_obj11, "&.".concat(outlinedInputClasses.sizeSmall), {
                    gap: rem(spacing[2]),
                    padding: "".concat(rem(4), " ").concat(rem(6)),
                    minHeight: rem(28),
                    height: 'auto'
                }), _define_property(_obj11, "& .".concat(outlinedInputClasses.notchedOutline), {
                    border: "1px solid ".concat(c.default.border)
                }), _define_property(_obj11, '&:hover', (_obj6 = {}, _define_property(_obj6, "& .".concat(outlinedInputClasses.notchedOutline), {
                    borderColor: c.hover.border
                }), _define_property(_obj6, "backgroundColor", c.hover.background), _obj6)), _define_property(_obj11, "&.".concat(outlinedInputClasses.disabled), (_obj7 = {
                    backgroundColor: c.disabled.background
                }, // MUI's own disabled styles set notchedOutline's border-color to
                // rgba(0,0,0,0.26), overriding the base rule above at equal
                // specificity by cascade order — restate the theme token explicitly.
                _define_property(_obj7, "& .".concat(outlinedInputClasses.notchedOutline), {
                    borderColor: c.disabled.border
                }), _define_property(_obj7, "& .".concat(inputAdornmentClasses.root, " .").concat(typographyClasses.root), {
                    color: c.disabled.text
                }), _define_property(_obj7, "& .".concat(inputAdornmentClasses.root, " .").concat(svgIconClasses.root), {
                    color: c.disabled.text
                }), _obj7)), // FormControl's context doesn't track `readOnly` (unlike `disabled`), so
                // this can't cascade to MuiFormLabel/MuiInputLabel/MuiFormHelperText the
                // way disabled does — Figma's Readonly hint/label/placeholder text values
                // are identical to Default's, so no sibling override is needed there.
                _define_property(_obj11, "&.".concat(inputBaseClasses.readOnly), _define_property({}, "& .".concat(outlinedInputClasses.notchedOutline), {
                    borderColor: c.readonly.border
                })), _define_property(_obj11, "&.".concat(outlinedInputClasses.focused), (_obj8 = {}, _define_property(_obj8, "& .".concat(outlinedInputClasses.notchedOutline), {
                    borderColor: c.active.border,
                    borderWidth: 1
                }), _define_property(_obj8, "backgroundColor", c.default.background), _obj8)), _define_property(_obj11, "&.".concat(outlinedInputClasses.error), _define_property({
                    '&:hover': (_obj9 = {}, _define_property(_obj9, "& .".concat(outlinedInputClasses.notchedOutline), {
                        borderColor: c.error.border
                    }), _define_property(_obj9, "backgroundColor", c.hover.background), _obj9)
                }, "&.".concat(outlinedInputClasses.focused), (_obj10 = {}, _define_property(_obj10, "& .".concat(outlinedInputClasses.notchedOutline), {
                    borderColor: c.error.border,
                    borderWidth: 1
                }), _define_property(_obj10, "backgroundColor", c.default.background), _obj10))), // When composing MuiSelect (via TextField's `select` prop), Select.tsx's
                // own `select:` override already supplies the full padding — this root's
                // padding would otherwise stack on top of it, inflating the box beyond
                // minHeight (measured: Small rendered at 33.25px instead of 28px). Must
                // come after the sizeSmall block above to win at equal specificity.
                _define_property(_obj11, "&:has(.".concat(selectClasses.select, ")"), {
                    padding: 0
                }), _obj11),
                notchedOutline: {
                    '& legend': {
                        maxWidth: '0'
                    }
                },
                multiline: // The textarea has to grow from the top, but an adornment beside it
                // should stay centred as it is on a single-line input. Without this it
                // inherits flex-start and rides above the text it belongs to.
                _define_property({
                    padding: rem(spacing[3]),
                    alignItems: 'flex-start',
                    // Root sets a fixed height for the single-line Medium input; a growable
                    // textarea must not inherit that.
                    height: 'auto'
                }, "& .".concat(inputAdornmentClasses.root), {
                    alignSelf: 'center'
                }),
                inputMultiline: {
                    height: 'auto'
                },
                input: (_obj12 = {
                    padding: 0,
                    fontSize: rem(14),
                    lineHeight: rem(18),
                    height: rem(20),
                    '&::placeholder': {
                        color: c.default.placeholderText,
                        opacity: 1
                    }
                }, _define_property(_obj12, "&.".concat(outlinedInputClasses.disabled), {
                    WebkitTextFillColor: c.disabled.text,
                    color: c.disabled.text,
                    '&::placeholder': {
                        WebkitTextFillColor: c.disabled.placeholderText,
                        color: c.disabled.placeholderText,
                        opacity: 1
                    }
                }), _define_property(_obj12, "&.".concat(inputBaseClasses.readOnly), {
                    color: c.readonly.text,
                    '&::placeholder': {
                        color: c.readonly.placeholderText,
                        opacity: 1
                    }
                }), _obj12),
                inputSizeSmall: {
                    padding: 0,
                    fontSize: rem(12),
                    lineHeight: rem(16)
                }
            },
            variants: [
                {
                    // Figma's "Corner Shape" axis: pill-shaped input (e.g. a search bar), used
                    // via `<TextField InputProps={{ 'data-corner-shape': 'round' }} />` — this
                    // reaches the underlying MuiOutlinedInput instance MUI renders internally.
                    // A data attribute, because OutlinedInput forwards unknown props to its
                    // root DOM element and React rejects a camelCase custom prop there.
                    props: {
                        'data-corner-shape': 'round'
                    },
                    style: (_obj13 = {
                        // MuiInputBase's root already boosts its own borderRadius with `&&`
                        // (see above); a plain declaration here loses that specificity fight,
                        // so this needs the same boost to win.
                        '&&': {
                            borderRadius: rem(32)
                        },
                        paddingLeft: rem(spacing[3]),
                        paddingRight: rem(spacing[3])
                    }, _define_property(_obj13, "& .".concat(outlinedInputClasses.notchedOutline), {
                        borderRadius: rem(32)
                    }), _define_property(_obj13, "&.".concat(outlinedInputClasses.sizeSmall), {
                        paddingLeft: rem(spacing[2]),
                        paddingRight: rem(spacing[2])
                    }), _obj13)
                }
            ]
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'medium',
                color: 'primary'
            }
        }
    };
    return overrides;
};

//# sourceMappingURL=TextField.js.map